// OCPP status values that mean a machine is actively connected/charging. The live
// charger-search payload uses the OCPP enum on `status` (CHARGING/PREPARING/...),
// so treat those as occupied and keep the literal "OCCUPIED" as a backend fallback.
const OCCUPIED_STATUSES = new Set(["OCCUPIED", "CHARGING", "PREPARING"]);
/**
 * Charge-transaction measures in the generated contract carry either `unit`
 * (durations, minutes) or `unitOfMeasure` (energy/SoC); normalise both to the
 * card's {@link IEssConnectorMeasure} shape. Returns undefined when there is no
 * usable value so the card renders its own "--" fallback.
 */
const normalizeMeasure = (measure) => {
    if (measure?.value === undefined || measure?.value === null) {
        return undefined;
    }
    return { unit: measure.unit ?? measure.unitOfMeasure, value: measure.value };
};
/**
 * Adapt the generated charger-search `Connectors` payload
 * (POST /projects/{projectId}/chargers/search -> chargerDetails.connectors) into
 * the presentational {@link IEssConnector} shape used by EssConnectorCard.
 *
 * Keeps the card (and its tests) decoupled from the wire contract: occupancy is
 * derived from the OCPP `status` (CHARGING/PREPARING, with an "OCCUPIED" fallback),
 * live/last-charge measures come from `chargeTransaction`, and the displayed status
 * prefers the activity-oriented `connectorStatus`. Fails closed (empty array) for a
 * missing/non-array payload.
 */
export const mapChargerConnectorsToEss = (connectors) => {
    if (!Array.isArray(connectors)) {
        return [];
    }
    return connectors.map((connector) => {
        const ocppStatus = (connector.status ?? connector.connectorStatus ?? "").toUpperCase();
        const isOccupied = OCCUPIED_STATUSES.has(ocppStatus);
        const transaction = connector.chargeTransaction;
        const energy = normalizeMeasure(transaction?.energyDelivered);
        return {
            chargeTimeRemaining: normalizeMeasure(transaction?.chargeTimeRemaining),
            connectedTo: connector.connectedTo,
            connectorId: connector.connectorId,
            connectorType: connector.connectorType,
            currentChargeEnergy: isOccupied ? energy : undefined,
            isOccupied,
            lastChargeDuration: normalizeMeasure(transaction?.lastChargeDuration),
            lastChargeEnergy: isOccupied ? undefined : energy,
            status: connector.connectorStatus ?? connector.status
        };
    });
};
